import { ChangeEvent, ReactNode, SyntheticEvent } from "react";
import { Box, Button, FormControlLabel, IconButton, Radio, RadioGroup } from "@mui/material";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { Trans } from "react-i18next";
import { Select, TextField, Typography } from "@appsflyer/fe-ui-core";
import AmplitudeLink from "../../services/AmplitudeLink";
import DragAndDropUpload from "../../main/components/DragDropUpload";
import { DJCodeBlock, DJSimpleCodeBlock } from "../../main/components/codeblock";
import { androidEvents, androidParams } from "../android/arrays";
import { iosEvents, iosParameter } from "../ios/arrays";
import { EvgenEventsObject } from "../IEvgen";
import { IOption } from "../SendInappEventParam";
import { SendInappStep } from "./types";

interface EventNameState {
  type: "predefined" | "custom";
  value: string;
}

interface EventParamState {
  type: "predefined" | "custom";
  value: string;
}

interface DesignStepParams {
  t: (key: string) => string;
  isFlutter: boolean;
  isReactNative: boolean;
  isAndroidFamily: boolean;
  tabValue: number;
  handleTabChange: (event: SyntheticEvent, newValue: number) => void;
  a11yProps: (index: number) => { id: string; "aria-controls": string };
  CustomTabPanel: (props: { children?: ReactNode; index: number; value: number }) => JSX.Element;
  EventsTable: (props: EvgenEventsObject) => JSX.Element;
  evgenEventsObject?: EvgenEventsObject;
  handleFileUpload: (newFile: File, target?: unknown) => void;
  eventName: EventNameState;
  eventParams: EventParamState[];
  hoveredRow: number | null;
  setHoveredRow: (row: number | null) => void;
  handleEventNameTypeChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleEventNameValueChange: (value: string | IOption) => void;
  handleParamTypeChange: (index: number, e: ChangeEvent<HTMLInputElement>) => void;
  handleValueChange: (index: number, value: string | IOption) => void;
  handleParamValueChange: (index: number, value: string) => void;
  handleRemoveParameter: (index: number) => void;
  handleAddParameter: () => void;
  options: IOption[];
  generatedEventCodePrimary: string;
  generatedEventCodeSecondary: string;
  reactNativeStep2Code: string;
}

export const createDesignStep = ({
  t,
  isFlutter,
  isReactNative,
  isAndroidFamily,
  tabValue,
  handleTabChange,
  a11yProps,
  CustomTabPanel,
  EventsTable,
  evgenEventsObject,
  handleFileUpload,
  eventName,
  eventParams,
  hoveredRow,
  setHoveredRow,
  handleEventNameTypeChange,
  handleEventNameValueChange,
  handleParamTypeChange,
  handleValueChange,
  handleParamValueChange,
  handleRemoveParameter,
  handleAddParameter,
  options,
  generatedEventCodePrimary,
  generatedEventCodeSecondary,
  reactNativeStep2Code,
}: DesignStepParams): SendInappStep => ({
  label: isFlutter
    ? t("ip_SendInApp_flutter_designInApp")
    : t("ip_SendInApp_android_designInApp"),
  component: (
    <>
      {isReactNative && (
        <Box p={2} bgcolor="warning.light" borderRadius={2} mb={2}>
          <Typography variant="body2" color="warning.dark">
            <Trans
              i18nKey="ip_CreateInApp_reactnative_step2_warning1"
              components={{ code: <code />, strong: <strong />, br: <br /> }}
            />
            <br />
            <Trans i18nKey="ip_CreateInApp_reactnative_step2_warning2" />
            <AmplitudeLink
              href="https://dev.appsflyer.com/hc/docs/rn_api#logadrevenue"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: "inherit", textDecoration: "underline" }}
            >
              {t("ip_CreateInApp_reactnative_step2_warning_link")}
            </AmplitudeLink>
            .
          </Typography>
        </Box>
      )}
      <Tabs value={tabValue} onChange={handleTabChange} aria-label="InApp Events Tabs Choose Type">
        <Tab label={t("ip_AppEvents_Evgen_upload")} {...a11yProps(0)} />
        <Tab label={t("ip_AppEvents_Evgen_single")} {...a11yProps(1)} />
      </Tabs>
      <CustomTabPanel value={tabValue} index={0}>
        <Typography variant="h3" sx={{ fontWeight: "400 !important" }}>
          AppsFlyer <AmplitudeLink href="https://evgen.appsflyer.com/">In-app event generator</AmplitudeLink>
        </Typography>
        <br />
        <DragAndDropUpload
          id="1"
          label={t("ip_AppEvents_Evgen_upload")}
          accept=".json"
          helperText={t("ipAppEvents_Evgen_jsononly")}
          onFileUpload={handleFileUpload}
        />
        {evgenEventsObject?.events?.length ? (
          <Box maxHeight={400} overflow={"auto"}>
            <EventsTable {...evgenEventsObject} />
          </Box>
        ) : null}
      </CustomTabPanel>
      <CustomTabPanel value={tabValue} index={1}>
        <Box mt={-1}>
          <Typography variant="h3">{t("ip_SendInApp_android_stepTwoHeading")}</Typography>
          <Box display="flex" alignItems="center" gap={2} mb={2} mt={1}>
            <RadioGroup row value={eventName.type} onChange={handleEventNameTypeChange}>
              <FormControlLabel
                control={<Radio />}
                label={t("ip_SendInApp_android_predefinedLabel")}
                value="predefined"
              />
              <FormControlLabel
                control={<Radio />}
                label={t("ip_SendInApp_android_customLabel")}
                value="custom"
              />
            </RadioGroup>

            {eventName.type === "predefined" ? (
              <Select
                id="event-name-select"
                variant="outlined"
                size="medium"
                value={eventName.value}
                onChange={handleEventNameValueChange}
                options={options}
                placeholder={
                  isFlutter
                    ? eventName.value || t("ip_SendInApp_ios_chooseOption")
                    : (isAndroidFamily
                        ? androidEvents.find((e) => e.android_constant_name === eventName.value)
                            ?.event_name
                        : iosEvents.find((e) => e.ios_constant_name === eventName.value)?.event_name) ||
                      t("ip_SendInApp_ios_chooseOption")
                }
              />
            ) : (
              <TextField
                variant="outlined"
                id="event-name-custom"
                value={eventName.value}
                onChange={(e) => handleEventNameValueChange(e.target.value)}
              />
            )}
          </Box>
          <Box>
            <Typography variant="h3">{t("ip_SendInApp_android_stepThreeHeading")}</Typography>
            <Box mt={1}>
              {eventParams.map((param, index) => (
                <Box
                  key={index}
                  display="flex"
                  alignItems="center"
                  gap={2}
                  onMouseEnter={() => setHoveredRow(index)}
                  onMouseLeave={() => setHoveredRow(null)}
                  mb={2}
                >
                  <RadioGroup row value={param.type} onChange={(e) => handleParamTypeChange(index, e)}>
                    <FormControlLabel
                      control={<Radio />}
                      label={t("ip_SendInApp_ios_predefined")}
                      value="predefined"
                    />
                    <FormControlLabel
                      control={<Radio />}
                      label={t("ip_SendInApp_ios_custom")}
                      value="custom"
                    />
                  </RadioGroup>

                  {param.type === "predefined" ? (
                    <Select
                      id={`predefined-param-${index}`}
                      variant="outlined"
                      size="medium"
                      placeholder={
                        isFlutter
                          ? param.value || t("adRevenue_choose_parameter")
                          : (isAndroidFamily
                              ? androidParams.find((p) => p.android_constant_name === param.value)
                                  ?.event_parameter_name
                              : iosParameter.find((p) => p.ios_constant_name === param.value)
                                  ?.event_parameter_name) || t("adRevenue_choose_parameter")
                      }
                      value={param.value}
                      onChange={(val) => handleValueChange(index, val)}
                      options={
                        isFlutter
                          ? (isAndroidFamily ? androidParams : iosParameter).map((p) => ({
                              value: p.event_parameter_name,
                              label: p.event_parameter_name,
                            }))
                          : isAndroidFamily
                            ? androidParams.map((p) => ({
                                value: p.android_constant_name,
                                label: p.event_parameter_name,
                              }))
                            : iosParameter.map((p) => ({
                                value: p.ios_constant_name,
                                label: p.event_parameter_name,
                              }))
                      }
                    />
                  ) : (
                    <TextField
                      variant="outlined"
                      id={`custom-param-${index}`}
                      placeholder={t("adRevenue_custom_parameter")}
                      value={param.value}
                      onChange={(e) => handleParamValueChange(index, e.target.value)}
                    />
                  )}

                  {hoveredRow === index && (
                    <IconButton onClick={() => handleRemoveParameter(index)}>
                      <DeleteOutlineOutlinedIcon color="error" />
                    </IconButton>
                  )}
                </Box>
              ))}
              {eventParams.length < 5 && (
                <Button
                  onClick={handleAddParameter}
                  variant="outlined"
                  size="large"
                  sx={{ borderRadius: "100px" }}
                  startIcon={<AddRoundedIcon />}
                >
                  {t("adRevenue_add_parameter")}
                </Button>
              )}
            </Box>
          </Box>
          <Box>
            <br />
            <Typography variant="h2">{t("ip_SendInApp_android_generatedEventCodeHeading")}</Typography>
            {isFlutter ? (
              <DJSimpleCodeBlock
                codePrimary={generatedEventCodePrimary}
                showLineNumbers={false}
                language="dart"
              />
            ) : isReactNative ? (
              <DJSimpleCodeBlock
                codePrimary={reactNativeStep2Code}
                showLineNumbers={true}
                wrapLongLines={false}
                language="javascript"
              />
            ) : (
              <DJCodeBlock
                codePrimary={generatedEventCodePrimary}
                codeSecondary={generatedEventCodeSecondary}
                showLineNumbers={true}
                wrapLongLines={false}
              />
            )}
          </Box>
        </Box>
      </CustomTabPanel>
    </>
  ),
  shouldDisplayNavigationButtons: true,
});
